import React, { useEffect, useState } from "react";
import { ErrorMessage, Field, FieldArray, Form, Formik } from "formik";
import { formatWhatsAppPath } from "../../../../hooks/helpers";
import { Button, Col, OverlayTrigger, Row, Tooltip } from "react-bootstrap";
import { MultiSelect, ReactSelect, Spinner, SpinnerLoader } from "@services/ui-components";
import PhonePicker from "../../../../../../../services/ui-components/src/PhonePicker/PhonePicker";
import { handlePaste } from "@me-team/host/src/utils/utils";
import MultipleImageCrop from "../../../ui-components/MultipleImageCrop/MultipleImageCrop";
import ImageCrop from "../../../../../../../services/ui-components/src/ImageCrop/ImageCrop";
import BranchModalLimit from "../../../Branch/BranchModalLimit";
import i18next from "i18next";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
    useCreateNewBranchMutation,
    useGetCompanyCreateBranchQuery,
    useGetCountriesQuery,
    useGetIsoCodeForBranchQuery,
} from "@me-team/host/main/branches/graphql/branches.hooks";
import { ApolloError, useReactiveVar } from "@apollo/client";
import { currentCompanyIdVar } from "@me-team/host/main/globalVar/state";
import ErrorService from "../../../../services/ErrorService";
import { BranchInput, BranchPhoneInput } from "@me-team/host/main/graphql/types";
import { isRefetchBranches } from "@me-team/host/main/branches/state";
import { ValidationErrorExtensions } from "@me-team/host/src/utils/type";
import { Option } from "../../../../interfaces/interfaces";
import { ErrorModalComponent } from "@me-pos/error-modal";
import { BusinessTypeSelector, WorkSchedulesSection } from "../../../index";
import { useGetZoneQuery } from "@me-team/host/main/shedule/graphql/shedule.hooks";
import { WorkingHoursWeekValueDto } from "@me-pos/schedule-modal";
import {
    mapWeekValueToWorkSchedulesInput,
    WORK_SCHEDULE_TYPE_GENERAL,
    WORK_SCHEDULE_TYPE_KITCHEN,
} from "../../../Me-menu/WorkingHours/helpers/workingHoursHelpers";
import { useBranchTypes } from "../../../../hooks/useBranchTypes";
import { BUSINESS_TYPE, BusinessType } from "../../../BusinessTypeSelector/businessType";
import CustomCreatableReactSelect from "../../../../../../../services/ui-components/src/select/CustomCreatableReactSelect";
import { foodBusinessBranchFormValidationSchema } from "./constants";
import { BusinessTypeOption } from "../../../BusinessTypeSelector/BusinessTypeOptions";
import { MultiOption } from "../../../../../../../services/ui-components/src/MultiSelect/MultiSelect";
import { GetLanguagesForCreateBranchQuery } from "@me-team/host/main/createBranch/graphql/createBranch.hooks";
import { getLanguageVersionOptions } from "../../../Me-menu/AllMenusPage/options";
import { useCountryCitiesPaginatedOptions } from "../../../../hooks/useCountryCitiesPaginatedOptions";
import BranchCitySelectFields from "../../components/BranchCitySelectFields";

interface FormValuesFoodBusiness {
    name: string;
    type?: string;
    description?: string;
    country: number;
    city: number;
    customCity?: string;
    address?: string;
    phones: BranchPhoneInput[];
    website?: string;
    images?: File | null;
    logo: File | null;
    instagram?: string;
    facebook?: string;
    youtube?: string;
    whatsapp?: string;
    generalWorkingHours: WorkingHoursWeekValueDto | null;
    kitchenWorkingHours: WorkingHoursWeekValueDto | null;
    languages: number[];
    primaryLanguage: number | null;
}

export interface CreateFoodBusinessBranchFormProps {
    selectedBusinessType: BusinessType;
    businessTypeOptions: BusinessTypeOption[];
    onBusinessTypeChange: (businessType: BusinessType) => void;
    languageVersionsData: GetLanguagesForCreateBranchQuery;
    canCreateBranch?: boolean;
}

const CreateFoodBusinessBranchForm: React.FC<CreateFoodBusinessBranchFormProps> = ({
    selectedBusinessType,
    businessTypeOptions,
    onBusinessTypeChange,
    languageVersionsData,
    canCreateBranch = true,
}) => {
    const { t } = useTranslation();
    const [countryId, setCountryId] = useState<number | null>(null);
    const navigate = useNavigate();
    const { data: countriesData } = useGetCountriesQuery();
    const [photos, setPhotos] = useState([null]);
    const currentCompanyId = useReactiveVar(currentCompanyIdVar);
    const [createBranch] = useCreateNewBranchMutation();
    const [showCustomCityInput, setShowCustomCityInput] = useState<boolean>(false);
    const [showModalLimit, setShowModalLimit] = useState<boolean>(false);
    const [isErrorModalOpen, setIsErrorModalOpen] = useState<number>(null);
    const { data: branchTypesData, loading: branchTypesLoading } = useBranchTypes();
    const businessTypes = branchTypesData?.branchTypes || null;
    const languageVersionOptions: Option[] = getLanguageVersionOptions(languageVersionsData?.relations?.languages);
    const companyPrimaryLanguageId = languageVersionsData?.user?.company?.primaryLanguage?.id ?? null;
    const companyLanguageIds = languageVersionsData?.user?.company?.languages?.map((language) => language.id) ?? [];
    const initialLanguageIds =
        companyPrimaryLanguageId != null && !companyLanguageIds.includes(companyPrimaryLanguageId)
            ? [...companyLanguageIds, companyPrimaryLanguageId]
            : companyLanguageIds;

    const { data: timeZoneData, loading: timeZoneLoading } = useGetZoneQuery({
        variables: { companyId: +currentCompanyId },
        skip: !currentCompanyId,
        context: {
            errorType: "local",
        },
    });

    const timeZone: string = timeZoneData?.user?.company?.timeZone?.name;

    const { data: companyData, loading: companyLoading } = useGetCompanyCreateBranchQuery({
        variables: { companyId: +currentCompanyId },
        skip: !currentCompanyId,
    });

    const { data: isoCodeData, loading: isoCodeLoading } = useGetIsoCodeForBranchQuery({
        variables: {
            companyId: Number(currentCompanyId),
        },
        skip: !currentCompanyId,
        context: {
            errorType: "local",
        },
        onError: (error: ApolloError) => {
            setIsErrorModalOpen(ErrorService.errorHandling(error));
        },
    });
    const phoneCode = isoCodeData?.user?.company?.country?.isoCode.toLowerCase();

    const countryOptions =
        countriesData?.relations.countries.map((country) => ({
            label: country.name,
            value: country.id,
        })) || [];

    const {
        cityOptions,
        selectedCityOption,
        isCitiesInitialLoading,
        isCitiesSearchLoading,
        onCityMenuOpen,
        onCityMenuClose,
        onCitySearchChange,
        onCityMenuScrollToBottom,
        resetCities,
    } = useCountryCitiesPaginatedOptions({
        countryId,
        selectedCity:
            companyData?.user?.company?.country?.id === countryId && companyData?.user?.company?.city
                ? {
                      id: companyData.user.company.city.id,
                      name: companyData.user.company.city.name,
                  }
                : null,
        includeCustomCityOption: true,
        customCityOptionLabel: t("Other city"),
        skip: !countryId,
    });

    const initialValues: FormValuesFoodBusiness = {
        name: "",
        type: "",
        country: companyData?.user?.company?.country?.id || null,
        city: companyData?.user?.company?.country?.id ? companyData?.user?.company?.city?.id : null,
        customCity: null,
        address: companyData?.user?.company?.address || "",
        phones: companyData?.user?.company?.phone ? [{ phone: companyData.user.company.phone }] : [{ phone: "" }],
        website: companyData?.user?.company?.website || "",
        description: "",
        images: null,
        logo: null,
        instagram: "",
        facebook: "",
        whatsapp: "",
        youtube: "",
        generalWorkingHours: null,
        kitchenWorkingHours: null,
        languages: initialLanguageIds,
        primaryLanguage: companyPrimaryLanguageId,
    };

    useEffect(() => {
        if (!countryId && companyData?.user?.company?.country?.id) {
            setCountryId(companyData.user.company.country.id);
        }
    }, [companyData, countryId]);

    const handleSubmit = async (
        values: FormValuesFoodBusiness,
        {
            setSubmitting,
            setFieldError,
        }: {
            setSubmitting: (isSubmitting: boolean) => void;
            setFieldError: (field: string, message: string) => void;
        }
    ) => {
        const selectedCityId = values.city ? Number(values.city) : null;

        if (!canCreateBranch) {
            setSubmitting(false);
            return;
        }

        const input: BranchInput = {
            category: BUSINESS_TYPE.FOOD,
            name: values.name,
            type: values.type,
            description: values.description,
            country: values.country,
            city: showCustomCityInput ? null : selectedCityId,
            otherCity: values?.customCity ? values?.customCity : null,
            address: values.address,
            phones: values.phones.map((phone) => ({ phone: phone.phone })),
            website: values.website,
            images: photos,
            logo: values.logo,
            languages: values.languages,
            primaryLanguage: values.primaryLanguage,
            socialLink: {
                instagram: values?.instagram || null,
                facebook: values?.facebook || null,
                youtube: values?.youtube || null,
                whatsapp: values?.whatsapp || null,
            },
        };

        const generalWorkSchedules = mapWeekValueToWorkSchedulesInput(
            values.generalWorkingHours,
            WORK_SCHEDULE_TYPE_GENERAL
        );

        const kitchenWorkSchedules = mapWeekValueToWorkSchedulesInput(
            values.kitchenWorkingHours,
            WORK_SCHEDULE_TYPE_KITCHEN
        );

        input.workSchedules = [...generalWorkSchedules, ...kitchenWorkSchedules];

        const validPhotos = photos.filter((photo) => photo != null);
        input.images = validPhotos;

        try {
            const { data } = await createBranch({
                variables: {
                    companyId: +currentCompanyId,
                    input,
                },
                context: {
                    errorType: "local",
                },
            });
            const newBranchId = data.createBranch.id;
            navigate(`/branches/${newBranchId}`, { state: { text: t("Created"), toast: true } });
            isRefetchBranches(true);
        } catch (error) {
            if (error?.graphQLErrors?.[0]?.extensions?.category === "branch.already_exists") {
                setFieldError("name", t("Branch with this name already exists!"));
                return;
            }
            if (error?.graphQLErrors?.[0]?.extensions?.category === "branch.tariff_error") {
                setShowModalLimit(true);
                isRefetchBranches(true);
            }
            const validationErrors = (error?.graphQLErrors?.[0]?.extensions as ValidationErrorExtensions | undefined)
                ?.validation;
            const websiteErrors = validationErrors?.website;
            if (Array.isArray(websiteErrors)) {
                const longError = websiteErrors.find((e) =>
                    e.message.includes("This value is too long. It should have 2040 characters or less.")
                );
                if (longError) {
                    setFieldError("website", t("The value length for this field must not exceed 2040 characters."));
                }
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handleCloseModalLimit = () => {
        setShowModalLimit(false);
    };

    const isBaseLoading = companyLoading || isoCodeLoading || timeZoneLoading || branchTypesLoading;

    return (
        <>
            <Formik
                initialValues={initialValues}
                validationSchema={foodBusinessBranchFormValidationSchema(t, true, true)}
                onSubmit={(values, actions) => handleSubmit(values, actions)}
                enableReinitialize
            >
                {({ setFieldValue, values, isSubmitting, errors, touched }) => {
                    values.whatsapp = formatWhatsAppPath(values.whatsapp);

                    const selectedLanguageIds = values.languages ?? [];

                    const primaryLanguageOptions: Option[] = (languageVersionOptions ?? []).filter((option) =>
                        selectedLanguageIds.includes(option.value)
                    );

                    const primaryLanguageValue =
                        primaryLanguageOptions.find((opt) => opt.value === values.primaryLanguage) || null;

                    return (
                        <Form>
                            <Row className="mt-3">
                                <Col lg={9} className="pe-lg-5">
                                    <div className="mb-4">
                                        <BusinessTypeSelector
                                            title={t("Business type")}
                                            selectedBusinessType={selectedBusinessType}
                                            options={businessTypeOptions}
                                            onChange={onBusinessTypeChange}
                                        />
                                    </div>
                                    <h5 className="fs-4 text-capitalize mb-4">{t("Basic information")}</h5>
                                    {isBaseLoading ? (
                                        <Spinner heightContent />
                                    ) : (
                                        <div>
                                            <div className="mb-3 mb-sm-4">
                                                <Row className="align-items-center">
                                                    <Col sm={3} md={2} className="mb-2 mb-sm-0">
                                                        <label
                                                            htmlFor="foodBusiness-name-field"
                                                            className="fs-7 fw-bold required-label"
                                                        >
                                                            {t("Establishment name (branch)")}
                                                        </label>
                                                    </Col>
                                                    <Col sm={9} md={10}>
                                                        <div className="position-relative">
                                                            <Field
                                                                id="foodBusiness-name-field"
                                                                name="name"
                                                                placeholder={t("Branch name")}
                                                                maxLength={50}
                                                                className={`form-control rounded-1 lh-22 pe-5 ${errors.name && touched.name ? "border-danger" : "border-secondary"}`}
                                                            />
                                                            <div className="pt-1 position-absolute top-50 translate-middle-y end-0 me-3">
                                                                <OverlayTrigger
                                                                    overlay={
                                                                        <Tooltip>
                                                                            {t(
                                                                                "This data will be displayed in the online booking form"
                                                                            )}
                                                                        </Tooltip>
                                                                    }
                                                                >
                                                                    <i className="bi bi-info-circle"></i>
                                                                </OverlayTrigger>
                                                            </div>
                                                        </div>
                                                    </Col>
                                                </Row>
                                                <Col sm={9} md={10} className="ms-auto">
                                                    <ErrorMessage name="name">
                                                        {(msg) => (
                                                            <p className="fs-7 py-1 px-2 text-danger m-0">{msg}</p>
                                                        )}
                                                    </ErrorMessage>
                                                </Col>
                                            </div>

                                            <Row className="mb-3 mb-sm-4">
                                                <Col sm={3} md={2} className="mb-2 mb-sm-0">
                                                    <span className="text-dark fw-bold">
                                                        <small>
                                                            <label
                                                                htmlFor="foodBusiness-name-field"
                                                                className="mb-0 text-dark form-label text-break"
                                                            >
                                                                {t("Type of Establishment")}
                                                            </label>
                                                        </small>
                                                    </span>
                                                </Col>
                                                <Col sm={9} md={10}>
                                                    <div className="position-relative">
                                                        <CustomCreatableReactSelect
                                                            optionsArray={businessTypes}
                                                            placeholder={t("Add type")}
                                                            setField={setFieldValue}
                                                            fieldName="type"
                                                            value="name"
                                                            isTooltip={true}
                                                            name={"type"}
                                                            id={"foodBusiness-name-field"}
                                                            maxLength={50}
                                                            createOptionLabel={t("Create the name of type")}
                                                            noOptionsText={t("Start typing the name")}
                                                            createOnBlur={true}
                                                        />

                                                        <div
                                                            className="position-absolute top-50 end-0 translate-middle-y pe-6"
                                                            style={{ height: "16px" }}
                                                        >
                                                            <OverlayTrigger
                                                                overlay={
                                                                    <Tooltip>
                                                                        {t("This data is displayed in the qr-menu")}
                                                                    </Tooltip>
                                                                }
                                                            >
                                                                <div className=" position-absolute top-50 translate-middle-y end-0 me-3">
                                                                    <i className="bi bi-info-circle"></i>
                                                                </div>
                                                            </OverlayTrigger>
                                                        </div>
                                                    </div>
                                                </Col>
                                                <div className="col-sm-9 col-md-10 ms-auto">
                                                    <span className="text-grey fs-7"> {values.type.length}/50</span>
                                                    <ErrorMessage name="type">
                                                        {(msg) => <p className="fs-7 m-0 p-1 text-danger">{msg}</p>}
                                                    </ErrorMessage>
                                                </div>
                                            </Row>

                                            <Row className="mb-3 mb-sm-4">
                                                <Col sm={3} md={2} className="mb-2 mb-sm-0">
                                                    <label
                                                        htmlFor="foodBusiness-description-field"
                                                        className="fs-7 fw-bold pt-2"
                                                    >
                                                        {t("Description of the establishment")}
                                                    </label>
                                                </Col>
                                                <Col sm={9} md={10}>
                                                    <div className="position-relative">
                                                        <Field
                                                            id="foodBusiness-description-field"
                                                            as="textarea"
                                                            name="description"
                                                            placeholder={t("Add Description")}
                                                            maxLength={600}
                                                            className="form-control pe-5 rounded-1"
                                                        />
                                                        <div className="pt-1 position-absolute top-0 end-0 mt-1 me-3">
                                                            <OverlayTrigger
                                                                overlay={
                                                                    <Tooltip>
                                                                        {t("This data is displayed in the qr-menu")}
                                                                    </Tooltip>
                                                                }
                                                            >
                                                                <i className="bi bi-info-circle"></i>
                                                            </OverlayTrigger>
                                                        </div>
                                                    </div>
                                                    <ErrorMessage name="description">
                                                        {(msg) => <p className="fs-7 p-1 text-danger m-0">{msg}</p>}
                                                    </ErrorMessage>
                                                    <div className="fs-7 text-grey text-start mt-1 px-1">
                                                        {`${values.description.length}/600`}
                                                    </div>
                                                </Col>
                                            </Row>

                                            <Row className="mb-3 mb-sm-4">
                                                <Col sm={3} md={2} className="mb-2 mb-sm-0">
                                                    <label
                                                        htmlFor="foodBusiness-country-field"
                                                        className="fs-7 fw-bold mt-sm-2 pt-1 required-label"
                                                    >
                                                        {t("country")}
                                                    </label>
                                                </Col>
                                                <Col sm={9} md={10}>
                                                    <ReactSelect
                                                        id="foodBusiness-country-field"
                                                        inputAutoComplete="new-password"
                                                        options={countryOptions}
                                                        value={
                                                            countryOptions.find(
                                                                (option) =>
                                                                    Number(option.value) === Number(values.country)
                                                            ) ?? null
                                                        }
                                                        onChange={(selectedOption: Option | null) => {
                                                            const selectedCountryId = selectedOption
                                                                ? Number(selectedOption.value)
                                                                : null;

                                                            setFieldValue("country", selectedCountryId);
                                                            setCountryId(selectedCountryId);
                                                            setFieldValue("city", null);
                                                            setFieldValue("customCity", null);
                                                            setShowCustomCityInput(false);
                                                            resetCities();
                                                        }}
                                                        placeholder={t("Select Country")}
                                                        isSearchable
                                                    />
                                                    <ErrorMessage name="country">
                                                        {(msg) => <p className="fs-7 p-1 text-danger m-0">{msg}</p>}
                                                    </ErrorMessage>
                                                </Col>
                                            </Row>

                                            <BranchCitySelectFields
                                                cityFieldId="foodBusiness-city-field"
                                                customCityFieldId="foodBusiness-customCity-field"
                                                cityOptions={cityOptions}
                                                selectedCityOption={selectedCityOption}
                                                showCustomCityInput={showCustomCityInput}
                                                setShowCustomCityInput={setShowCustomCityInput}
                                                isLoading={isCitiesInitialLoading || isCitiesSearchLoading}
                                                onMenuOpen={onCityMenuOpen}
                                                onMenuClose={onCityMenuClose}
                                                onInputChange={onCitySearchChange}
                                                onMenuScrollToBottom={onCityMenuScrollToBottom}
                                                labelColumnProps={{ sm: 3, md: 2 }}
                                                inputColumnProps={{ sm: 9, md: 10 }}
                                            />

                                            <Row className="mb-3 mb-sm-4">
                                                <Col sm={3} md={2} className="mb-2 mb-sm-0">
                                                    <label
                                                        htmlFor="foodBusiness-address-field"
                                                        className="fs-7 fw-bold mt-2 pt-1 required-label"
                                                    >
                                                        {t("address")}
                                                    </label>
                                                </Col>
                                                <Col sm={9} md={10}>
                                                    <div className="position-relative">
                                                        <Field
                                                            id="foodBusiness-address-field"
                                                            name="address"
                                                            autoComplete="address-line1"
                                                            placeholder={t("Enter your location")}
                                                            maxLength={200}
                                                            className={`form-control pe-5 ${errors.address && touched.address ? "border-danger" : "border-secondary"}`}
                                                        />
                                                        <div className="pt-1 position-absolute top-50 translate-middle-y end-0 me-3">
                                                            <OverlayTrigger
                                                                overlay={
                                                                    <Tooltip>
                                                                        {t("This data is displayed in the qr-menu")}
                                                                    </Tooltip>
                                                                }
                                                            >
                                                                <i className="bi bi-info-circle"></i>
                                                            </OverlayTrigger>
                                                        </div>
                                                    </div>
                                                    <ErrorMessage name="address">
                                                        {(msg) => <p className="fs-7 p-1 text-danger m-0">{msg}</p>}
                                                    </ErrorMessage>
                                                </Col>
                                            </Row>

                                            <div className="form-group">
                                                <Row className="mb-2">
                                                    <Col sm={3} md={2}>
                                                        <label className="fs-7 fw-bold mt-sm-2 pt-1 required-label">
                                                            {t("Phone")}
                                                        </label>
                                                    </Col>
                                                    <Col sm={9} md={10}>
                                                        <FieldArray
                                                            name="phones"
                                                            render={({ remove, push }) => (
                                                                <>
                                                                    {values.phones.map((phone: any, index: number) => {
                                                                        return (
                                                                            <div
                                                                                className="d-flex align-items-center w-100 mb-3"
                                                                                key={index}
                                                                            >
                                                                                <div className="w-100">
                                                                                    <PhonePicker
                                                                                        id={`foodBusiness-phones.${index}.phone-field`}
                                                                                        key={`phones.${index}.phone`}
                                                                                        name={`phones.${index}.phone`}
                                                                                        value={phone.phone}
                                                                                        defaultCountry={phoneCode}
                                                                                    />
                                                                                    <ErrorMessage
                                                                                        name={`phones.${index}.phone`}
                                                                                    >
                                                                                        {(msg) => (
                                                                                            <p className="fs-7 p-1 text-danger m-0">
                                                                                                {msg}
                                                                                            </p>
                                                                                        )}
                                                                                    </ErrorMessage>
                                                                                </div>
                                                                                {index !== 0 && (
                                                                                    <Button
                                                                                        type="button"
                                                                                        variant="outline-secondary"
                                                                                        className="h-100 ms-3 px-3 py-6 fs-normal rounded-1 align-self-start"
                                                                                        onClick={() => remove(index)}
                                                                                        disabled={isSubmitting}
                                                                                    >
                                                                                        <i className="bi bi-trash3 text-dark"></i>
                                                                                    </Button>
                                                                                )}

                                                                                {index === 0 && (
                                                                                    <Button
                                                                                        type="button"
                                                                                        variant="outline-secondary"
                                                                                        className="h-100 ms-3 px-3 py-6 fs-normal rounded-1 align-self-start"
                                                                                        onClick={() => push("")}
                                                                                        disabled={isSubmitting}
                                                                                    >
                                                                                        <i className="bi bi-plus-lg text-dark"></i>
                                                                                    </Button>
                                                                                )}
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </>
                                                            )}
                                                        />
                                                    </Col>
                                                </Row>
                                            </div>

                                            <Row className="mb-3 mb-sm-4">
                                                <Col sm={3} md={2} className="mb-2 mb-sm-0">
                                                    <label
                                                        htmlFor="foodBusiness-website-field"
                                                        className="fs-7 fw-bold mt-2 pt-1"
                                                    >
                                                        {t("website")}
                                                    </label>
                                                </Col>
                                                <Col sm={9} md={10}>
                                                    <Field
                                                        id="foodBusiness-website-field"
                                                        name="website"
                                                        placeholder={t("Site address")}
                                                        className={`form-control rounded-1 lh-22 pe-5 ${errors.website && touched.website ? "border-danger" : "border-secondary"}`}
                                                        maxLength={2040}
                                                        onPaste={(e: React.ClipboardEvent<HTMLInputElement>) =>
                                                            handlePaste(e, 2040)
                                                        }
                                                    />
                                                    <ErrorMessage name="website">
                                                        {(msg) => <p className="fs-7 p-1 text-danger m-0">{msg}</p>}
                                                    </ErrorMessage>
                                                </Col>
                                            </Row>
                                            <Row className="mb-3 mb-sm-4">
                                                <Col sm={3} md={2} className="text-start mb-2 mb-sm-0">
                                                    <label
                                                        htmlFor="foodBusiness-languages-field"
                                                        className="required-label"
                                                    >
                                                        <span className="fs-7 fw-bold text-start">
                                                            {t("Language versions of the menu")}
                                                        </span>
                                                    </label>
                                                </Col>
                                                <Col sm={9} md={10}>
                                                    <MultiSelect
                                                        id="foodBusiness-languages-field"
                                                        name="languages"
                                                        placeholder={t("Select additional menu languages")}
                                                        options={languageVersionOptions ?? []}
                                                        value={languageVersionOptions.filter((option) =>
                                                            selectedLanguageIds.includes(option.value)
                                                        )}
                                                        onChange={(selectedOptions: MultiOption[] | null) => {
                                                            const newIds = (selectedOptions ?? []).map(
                                                                (opt) => opt.value
                                                            );
                                                            setFieldValue("languages", newIds);
                                                            if (
                                                                values.primaryLanguage != null &&
                                                                !newIds.includes(values.primaryLanguage)
                                                            ) {
                                                                setFieldValue("primaryLanguage", null);
                                                            }
                                                        }}
                                                        isSearchable
                                                    />
                                                    <ErrorMessage name="languages">
                                                        {(msg) => <div className="invalid-feedback d-block">{msg}</div>}
                                                    </ErrorMessage>
                                                </Col>
                                            </Row>
                                            <Row className="mb-3 mb-sm-4">
                                                <Col sm={3} md={2} className="text-start mb-2 mb-sm-0">
                                                    <label
                                                        htmlFor="foodBusiness-primaryLanguage-field"
                                                        className="required-label"
                                                    >
                                                        <span className="fs-7 fw-bold text-start">
                                                            {t("Main language menu")}
                                                        </span>
                                                    </label>
                                                </Col>
                                                <Col sm={9} md={10}>
                                                    <ReactSelect
                                                        id="foodBusiness-primaryLanguage-field"
                                                        name="primaryLanguage"
                                                        value={primaryLanguageValue}
                                                        options={primaryLanguageOptions}
                                                        placeholder={t("Choose a language")}
                                                        onChange={(option: Option | null) =>
                                                            setFieldValue(
                                                                "primaryLanguage",
                                                                option ? option.value : null
                                                            )
                                                        }
                                                        isSearchable={true}
                                                        disabled={!primaryLanguageOptions?.length}
                                                    />
                                                    <ErrorMessage name="primaryLanguage">
                                                        {(msg) => <div className="invalid-feedback d-block">{msg}</div>}
                                                    </ErrorMessage>
                                                </Col>
                                            </Row>
                                            <Row className="mb-3 mb-sm-4">
                                                <Col sm={3} md={2} className="mb-2 mb-sm-0">
                                                    <label htmlFor="foodBusiness-Photo-field" className="fs-7 fw-bold">
                                                        {t("Photo of the branch")}
                                                    </label>
                                                </Col>
                                                <Col sm={9} md={10}>
                                                    <div className="mb-2">
                                                        <p className="fs-7 mb-0">
                                                            {t(
                                                                "Add jpeg images of the branch premises, interior, etc."
                                                            )}
                                                            <OverlayTrigger
                                                                overlay={
                                                                    <Tooltip>
                                                                        {t(
                                                                            "This data will be displayed in the online booking form"
                                                                        )}
                                                                    </Tooltip>
                                                                }
                                                            >
                                                                <i className="bi bi-info-circle ms-1 fs-7 pt-1"></i>
                                                            </OverlayTrigger>
                                                        </p>
                                                    </div>
                                                    <Row className="gx-0">
                                                        <MultipleImageCrop
                                                            key={photos[0]}
                                                            photosArray={photos}
                                                            setPhotos={setPhotos}
                                                            setFieldValue={setFieldValue}
                                                        />
                                                    </Row>
                                                    <div className="mt-2">
                                                        <p className="fs-7 text-grey mb-6">{t("max-size")}</p>
                                                    </div>
                                                </Col>
                                            </Row>

                                            <div className="d-flex gap-2 mb-4 pt-sm-6">
                                                <h5 className="fs-4 text-capitalize">{t("Social Media")}</h5>
                                                <OverlayTrigger
                                                    overlay={
                                                        <Tooltip>{t("This data is displayed in the qr-menu")}</Tooltip>
                                                    }
                                                >
                                                    <i className="bi bi-info-circle"></i>
                                                </OverlayTrigger>
                                            </div>

                                            <Row className="mb-3 mb-sm-4">
                                                <Col sm={3} md={2} className="mb-2 mb-sm-0">
                                                    <label
                                                        htmlFor="foodBusiness-instagram-field"
                                                        className="fs-7 fw-bold pt-2"
                                                    >
                                                        {t("Instagram")}
                                                    </label>
                                                </Col>
                                                <Col sm={9} md={10}>
                                                    <Field
                                                        name="instagram"
                                                        id="foodBusiness-instagram-field"
                                                        placeholder={t("Add link")}
                                                        className="form-control"
                                                    />
                                                    <ErrorMessage
                                                        name="instagram"
                                                        component="div"
                                                        className="invalid-feedback"
                                                    />
                                                </Col>
                                            </Row>

                                            <Row className="mb-3 mb-sm-4">
                                                <Col sm={3} md={2} className="mb-2 mb-sm-0">
                                                    <label
                                                        htmlFor="foodBusiness-facebook-field"
                                                        className="fs-7 fw-bold pt-2"
                                                    >
                                                        {t("Facebook")}
                                                    </label>
                                                </Col>
                                                <Col sm={9} md={10}>
                                                    <Field
                                                        name="facebook"
                                                        id="foodBusiness-facebook-field"
                                                        placeholder={t("Add link")}
                                                        className="form-control"
                                                    />
                                                    <ErrorMessage
                                                        name="facebook"
                                                        component="div"
                                                        className="invalid-feedback"
                                                    />
                                                </Col>
                                            </Row>

                                            <Row className="mb-3 mb-sm-4">
                                                <Col sm={3} md={2} className="mb-2 mb-sm-0">
                                                    <label
                                                        htmlFor="foodBusiness-youtube-field"
                                                        className="fs-7 fw-bold pt-2"
                                                    >
                                                        {t("Youtube")}
                                                    </label>
                                                </Col>
                                                <Col sm={9} md={10}>
                                                    <Field
                                                        name="youtube"
                                                        id="foodBusiness-youtube-field"
                                                        placeholder={t("Add link")}
                                                        className="form-control"
                                                    />
                                                    <ErrorMessage
                                                        name="youtube"
                                                        component="div"
                                                        className="invalid-feedback"
                                                    />
                                                </Col>
                                            </Row>

                                            <Row className="mb-3 mb-sm-4 pb-sm-6">
                                                <Col sm={3} md={2} className="mb-2 mb-sm-0">
                                                    <label
                                                        htmlFor="foodBusiness-whatsapp-field"
                                                        className="fs-7 fw-bold pt-2"
                                                    >
                                                        {t("WhatsApp")}
                                                    </label>
                                                </Col>
                                                <Col sm={9} md={10}>
                                                    <Field
                                                        name="whatsapp"
                                                        id="foodBusiness-whatsapp-field"
                                                        placeholder={t("Add link")}
                                                        className="form-control"
                                                    />
                                                    <ErrorMessage
                                                        name="whatsapp"
                                                        component="div"
                                                        className="invalid-feedback"
                                                    />
                                                </Col>
                                            </Row>

                                            {!!timeZone && (
                                                <WorkSchedulesSection
                                                    title="WorkingHours.Work Schedule"
                                                    timeZone={timeZone}
                                                    values={values}
                                                    errors={errors}
                                                    touched={touched}
                                                    setFieldValue={setFieldValue}
                                                    fieldNames={{
                                                        general: "generalWorkingHours",
                                                        kitchen: "kitchenWorkingHours",
                                                    }}
                                                />
                                            )}
                                        </div>
                                    )}
                                </Col>
                                <Col lg={3} className="ms-auto ps-lg-0">
                                    <h5 className="fs-4 mb-4">{t("logo")}</h5>
                                    <ImageCrop
                                        setFieldValue={setFieldValue}
                                        fieldName="logo"
                                        modalTitle={t("Branch logo")}
                                    />
                                </Col>
                            </Row>
                            <div className="d-sm-flex my-4 mt-lg-0">
                                <Button
                                    variant="outline-primary"
                                    className="fw-normal mb-3 mb-sm-0 me-3 custom-btn rounded-1"
                                    onClick={() => navigate(-1)}
                                >
                                    {t("Cancel")}
                                </Button>
                                <Button
                                    type="submit"
                                    variant="primary"
                                    disabled={isSubmitting || !canCreateBranch}
                                    className="fw-bold custom-btn rounded-1"
                                >
                                    {isSubmitting ? (
                                        <SpinnerLoader variant="light" loadingText={t("Saving...")} />
                                    ) : (
                                        t("Save")
                                    )}
                                </Button>
                            </div>
                        </Form>
                    );
                }}
            </Formik>
            <BranchModalLimit show={showModalLimit} handleClose={handleCloseModalLimit} />
            {isErrorModalOpen ? (
                <ErrorModalComponent
                    i18n={i18next}
                    onClose={() => {
                        setIsErrorModalOpen(null);
                    }}
                    isOpen={!!isErrorModalOpen}
                    currentError={isErrorModalOpen}
                />
            ) : null}
        </>
    );
};

export default CreateFoodBusinessBranchForm;
